import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChurchCombobox } from '@/components/church-combobox';
import { CouponDetailDialog } from '@/components/coupon-detail-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/shared/form-field';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Listbox } from '@/components/ui/listbox';
import { toast } from '@/components/ui/toast';
import { api, type OffsetPage } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import {
  couponDateToEndUtc,
  couponDateToStartUtc,
  couponDiscountLabel,
  couponDurationLabel,
  couponIsoToCalendarDate,
  couponUsesLabel,
  durationPresetFromCoupon,
  type PlatformCoupon,
} from '@/lib/coupon';
import { useAuthStore } from '@/stores/auth-store';
import { formatMoney } from '@/utils/format';

type DurationPreset = 'once' | '1' | '3' | '6' | '12' | 'custom' | 'forever';

const durationFromPreset = (
  preset: DurationPreset,
  customMonths: string,
): { durationType: PlatformCoupon['durationType']; durationMonths: number | null } => {
  if (preset === 'once') return { durationType: 'once', durationMonths: null };
  if (preset === 'forever') return { durationType: 'forever', durationMonths: null };
  if (preset === 'custom') {
    const months = Number(customMonths);
    return { durationType: 'repeating', durationMonths: Number.isFinite(months) ? months : null };
  }
  return { durationType: 'repeating', durationMonths: Number(preset) };
};

export const CouponsPage = () => {
  const { t } = useTranslation('admin');
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const isAdmin = useAuthStore((state) => state.user?.platformRole) === 'platform_admin';

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [kind, setKind] = useState<'discount' | 'referral'>('discount');
  const [discountType, setDiscountType] = useState<'percent' | 'amount' | 'free_months'>('percent');
  const [discountValue, setDiscountValue] = useState('10');
  const [durationPreset, setDurationPreset] = useState<DurationPreset>('once');
  const [customMonths, setCustomMonths] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [validFrom, setValidFrom] = useState<string | null>(null);
  const [validUntil, setValidUntil] = useState<string | null>(null);
  const [assignedChurchId, setAssignedChurchId] = useState<string | null>(null);
  const [assignedChurchLabel, setAssignedChurchLabel] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  const resetForm = () => {
    setEditingId(null);
    setCode('');
    setKind('discount');
    setDiscountType('percent');
    setDiscountValue('10');
    setDurationPreset('once');
    setCustomMonths('');
    setMaxRedemptions('');
    setValidFrom(null);
    setValidUntil(null);
    setAssignedChurchId(null);
    setAssignedChurchLabel(null);
    setNotes('');
  };

  const fillForm = (coupon: PlatformCoupon) => {
    const duration = durationPresetFromCoupon(coupon);
    setEditingId(coupon.id);
    setCode(coupon.code);
    setKind(coupon.kind);
    setDiscountType(coupon.discountType);
    setDiscountValue(String(coupon.discountValue));
    setDurationPreset(duration.preset);
    setCustomMonths(duration.customMonths);
    setMaxRedemptions(coupon.maxRedemptions != null ? String(coupon.maxRedemptions) : '');
    setValidFrom(couponIsoToCalendarDate(coupon.validFrom));
    setValidUntil(couponIsoToCalendarDate(coupon.validUntil));
    setAssignedChurchId(coupon.assignedChurchId);
    setAssignedChurchLabel(coupon.assignedChurchName);
    setNotes(coupon.notes ?? '');
  };

  const couponPayload = () => ({
    code,
    kind,
    discountType,
    discountValue: Number(discountValue),
    ...(discountType === 'free_months'
      ? { durationType: 'once' as const, durationMonths: null }
      : durationFromPreset(durationPreset, customMonths)),
    assignedChurchId,
    maxRedemptions: maxRedemptions.trim() ? Number(maxRedemptions) : null,
    validFrom: validFrom ? couponDateToStartUtc(validFrom) : null,
    validUntil: validUntil ? couponDateToEndUtc(validUntil) : null,
    notes: notes.trim() || null,
  });

  const coupons = useQuery({
    queryKey: ['platform-coupons'],
    queryFn: () =>
      api.get<{ data: PlatformCoupon[]; page: OffsetPage }>('/platform/billing/coupons', {
        query: { page: 1, pageSize: 100 },
      }),
    enabled: isAdmin,
  });

  const saveCoupon = useMutation({
    mutationFn: () => {
      if (editingId) {
        return api.patch<PlatformCoupon>(`/platform/billing/coupons/${editingId}`, couponPayload());
      }
      return api.post<PlatformCoupon>('/platform/billing/coupons', couponPayload());
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['platform-coupons'] });
      toast.success(editingId ? t('billing.coupons.updated') : t('billing.coupons.created'));
      setOpen(false);
      resetForm();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const toggleCoupon = useMutation({
    mutationFn: (input: { id: string; active: boolean }) =>
      api.patch<PlatformCoupon>(`/platform/billing/coupons/${input.id}`, { active: input.active }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['platform-coupons'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const selectedCoupon = coupons.data?.data.find((row) => row.id === selectedId) ?? null;

  const columns: DataTableColumn<PlatformCoupon>[] = [
    { id: 'code', header: t('billing.coupons.code'), cell: (row) => row.code },
    {
      id: 'kind',
      header: t('billing.coupons.kind'),
      cell: (row) => t(`billing.coupons.kinds.${row.kind}`),
    },
    {
      id: 'discount',
      header: t('billing.coupons.discountType'),
      cell: (row) => couponDiscountLabel(row, t, (minor) => formatMoney(minor, 'GHS')),
    },
    {
      id: 'duration',
      header: t('billing.coupons.duration'),
      cell: (row) => couponDurationLabel(row, t),
    },
    {
      id: 'church',
      header: t('billing.coupons.assignedChurch'),
      cell: (row) => row.assignedChurchName ?? t('billing.coupons.assignedChurchAny'),
    },
    {
      id: 'uses',
      header: t('billing.coupons.redemptions'),
      cell: (row) => couponUsesLabel(row, t),
    },
    {
      id: 'status',
      header: t('billing.coupons.active'),
      cell: (row) => (
        <Badge variant={row.active ? 'success' : 'neutral'}>
          {row.active ? t('billing.coupons.active') : t('billing.coupons.inactive')}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('billing.coupons.title')}
        description={t('billing.coupons.description')}
        headerActions={
          isAdmin ? (
            <Button
              onClick={() => {
                resetForm();
                setOpen(true);
              }}
            >
              {t('billing.coupons.create')}
            </Button>
          ) : undefined
        }
      />

      {!isAdmin ? (
        <p className="text-body-sm text-fg-secondary">{t('billing.coupons.readOnlyNotice')}</p>
      ) : null}

      {isAdmin ? (
        <DataTable
          columns={columns}
          rows={coupons.data?.data ?? []}
          rowKey={(row) => row.id}
          loading={coupons.isLoading}
          empty={t('billing.coupons.empty')}
          onRowClick={(row) => setSelectedId(row.id)}
          rowActions={(row) => (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => toggleCoupon.mutate({ id: row.id, active: !row.active })}
            >
              {row.active ? t('billing.coupons.deactivate') : t('billing.coupons.activate')}
            </Button>
          )}
        />
      ) : null}

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) resetForm();
        }}
      >
        <DialogContent className="overflow-visible" size="lg">
          <DialogHeader>
            <DialogTitle>
              {editingId ? t('billing.coupons.editTitle') : t('billing.coupons.createTitle')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody
            className="overflow-visible max-h-[min(75dvh,44rem)]"
            contentClassName="flex flex-col gap-4"
          >
            <div className="grid gap-4 sm:grid-cols-2 sm:items-start">
              <FormField label={t('billing.coupons.code')}>
                {(field) => (
                  <Input
                    id={field.id}
                    value={code}
                    onChange={(event) => setCode(event.target.value.toUpperCase())}
                  />
                )}
              </FormField>
              <FormField label={t('billing.coupons.kind')}>
                {(field) => (
                  <Listbox
                    id={field.id}
                    value={kind}
                    onChange={(value) => value && setKind(value)}
                    popoverPortal="dialog"
                    options={[
                      { value: 'discount', label: t('billing.coupons.kinds.discount') },
                      { value: 'referral', label: t('billing.coupons.kinds.referral') },
                    ]}
                  />
                )}
              </FormField>
            </div>
            <FormField
              label={t('billing.coupons.assignedChurch')}
              hint={t('billing.coupons.assignedChurchHint')}
              optionalLabel={t('billing.coupons.optional')}
            >
              {(field) => (
                <ChurchCombobox
                  id={field.id}
                  value={assignedChurchId}
                  selectedLabel={assignedChurchLabel}
                  onChange={(churchId) => {
                    setAssignedChurchId(churchId);
                    if (!churchId) setAssignedChurchLabel(null);
                  }}
                  placeholder={t('billing.coupons.assignedChurchPlaceholder')}
                  emptyMessage={t('billing.coupons.assignedChurchEmpty')}
                  clearLabel={t('billing.coupons.clearChurch')}
                  popoverPortal="dialog"
                />
              )}
            </FormField>
            <div className="grid gap-4 sm:grid-cols-2 sm:items-start">
              <FormField label={t('billing.coupons.discountType')}>
                {(field) => (
                  <Listbox
                    id={field.id}
                    value={discountType}
                    onChange={(value) => {
                      if (!value) return;
                      setDiscountType(value);
                      if (value === 'free_months') setDurationPreset('once');
                    }}
                    popoverPortal="dialog"
                    options={[
                      { value: 'percent', label: t('billing.coupons.discountTypes.percent') },
                      { value: 'amount', label: t('billing.coupons.discountTypes.amount') },
                      { value: 'free_months', label: t('billing.coupons.discountTypes.free_months') },
                    ]}
                  />
                )}
              </FormField>
              <FormField label={t('billing.coupons.discountValue')}>
                {(field) => (
                  <Input
                    id={field.id}
                    inputMode="numeric"
                    value={discountValue}
                    onChange={(event) => setDiscountValue(event.target.value)}
                  />
                )}
              </FormField>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 sm:items-start">
              {discountType !== 'free_months' ? (
                <FormField
                  label={t('billing.coupons.duration')}
                  hint={t('billing.coupons.durationHint')}
                >
                  {(field) => (
                    <Listbox
                      id={field.id}
                      value={durationPreset}
                      onChange={(value) => value && setDurationPreset(value)}
                      popoverPortal="dialog"
                      options={[
                        { value: 'once', label: t('billing.coupons.durations.once') },
                        { value: '1', label: t('billing.coupons.durations.1') },
                        { value: '3', label: t('billing.coupons.durations.3') },
                        { value: '6', label: t('billing.coupons.durations.6') },
                        { value: '12', label: t('billing.coupons.durations.12') },
                        { value: 'custom', label: t('billing.coupons.durations.custom') },
                        { value: 'forever', label: t('billing.coupons.durations.forever') },
                      ]}
                    />
                  )}
                </FormField>
              ) : null}
              {discountType !== 'free_months' && durationPreset === 'custom' ? (
                <FormField label={t('billing.coupons.durationMonths')}>
                  {(field) => (
                    <Input
                      id={field.id}
                      inputMode="numeric"
                      value={customMonths}
                      onChange={(event) => setCustomMonths(event.target.value)}
                    />
                  )}
                </FormField>
              ) : null}
              <FormField label={t('billing.coupons.maxRedemptions')}>
                {(field) => (
                  <Input
                    id={field.id}
                    inputMode="numeric"
                    value={maxRedemptions}
                    onChange={(event) => setMaxRedemptions(event.target.value)}
                    placeholder={t('billing.coupons.unlimited')}
                  />
                )}
              </FormField>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 sm:items-start">
              <FormField
                label={t('billing.coupons.validFrom')}
                hint={t('billing.coupons.validFromHint')}
                optionalLabel={t('billing.coupons.optional')}
              >
                {(field) => (
                  <DatePicker
                    id={field.id}
                    value={validFrom}
                    onChange={setValidFrom}
                    max={validUntil ?? undefined}
                    placeholder={t('billing.coupons.validFromPlaceholder')}
                    clearable
                    clearLabel={t('billing.coupons.clearDate')}
                    popoverPortal="dialog"
                    calendarLabels={{
                      month: t('billing.coupons.calendarMonth'),
                      year: t('billing.coupons.calendarYear'),
                      previousMonth: t('billing.coupons.calendarPrevious'),
                      nextMonth: t('billing.coupons.calendarNext'),
                    }}
                  />
                )}
              </FormField>
              <FormField
                label={t('billing.coupons.validUntil')}
                hint={t('billing.coupons.validUntilHint')}
                optionalLabel={t('billing.coupons.optional')}
              >
                {(field) => (
                  <DatePicker
                    id={field.id}
                    value={validUntil}
                    onChange={setValidUntil}
                    min={validFrom ?? undefined}
                    placeholder={t('billing.coupons.validUntilPlaceholder')}
                    clearable
                    clearLabel={t('billing.coupons.clearDate')}
                    popoverPortal="dialog"
                    calendarLabels={{
                      month: t('billing.coupons.calendarMonth'),
                      year: t('billing.coupons.calendarYear'),
                      previousMonth: t('billing.coupons.calendarPrevious'),
                      nextMonth: t('billing.coupons.calendarNext'),
                    }}
                  />
                )}
              </FormField>
            </div>
            <FormField label={t('billing.coupons.notes')}>
              {(field) => (
                <Input id={field.id} value={notes} onChange={(event) => setNotes(event.target.value)} />
              )}
            </FormField>
          </DialogBody>
          <DialogFooter className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button loading={saveCoupon.isPending} onClick={() => saveCoupon.mutate()}>
              {t('billing.coupons.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CouponDetailDialog
        coupon={selectedCoupon}
        open={selectedId !== null}
        onOpenChange={(next) => !next && setSelectedId(null)}
        canManage={isAdmin}
        onEdit={(coupon) => {
          fillForm(coupon);
          setSelectedId(null);
          setOpen(true);
        }}
      />
    </div>
  );
};
